// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ObservableOutput, TaxonomieOutput, ObservableReportLevel, ObjectTypeV1 as ObjectType } from 'thehive-sdk';
import DisplayTag from '@Organisms/DisplayTag/DisplayTag';
import { useLocalMessage } from '@Helpers/locale';
import { classList } from '@Helpers/className';
import { ObservableReportTag } from '@Organisms/Observable/ObservableReportTag/ObservableReportTag';
import { formatTags } from '@Helpers/tags';
import { ObservableReportLevelColors } from '@Constants/colors';
import { ObservableEventList } from '@Organisms/Observable/eventList';
import RouteBuilder from '@Services/RouteBuilder';
import { formatObservableTitle, isAlreadyFang } from '@Helpers/observable';
import { ColorLabel } from '@Atoms/Info/ColorLabel/ColorLabel';
import { ICONS } from '@Constants/iconsApp';
import { PreviewButton } from '@Atoms/Buttons';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { ScopeContext } from '@Context/ScopeProvider';

import './ObservableListTitle.scss';

interface ReportTagType extends Partial<TaxonomieOutput> {
    analyzerId: string;
    index: number;
}

interface ObservableListTitleProps {
    data: ObservableOutput;
    eventList?: ObservableEventList;
    entityId?: string;
    entityType?: ObjectType;
    onTagClick?: (tagValue: string[], multiple: boolean) => void;
    onDataTypeClick?: (dataType: string) => void;
    onPreviewClick?: () => void;
    reportsIsOpen?: boolean;
    tagsIsOpen?: boolean;
    disabled?: boolean;
}

export const ObservableListTitle = ({
    data,
    onTagClick,
    eventList,
    entityId,
    entityType,
    onDataTypeClick,
    reportsIsOpen = true,
    tagsIsOpen = true,
    onPreviewClick,
    disabled,
}: ObservableListTitleProps): ReactElement => {
    const local = useLocalMessage();
    const { _id: observableId, attachment, data: obsData = '', tags = [], reports = {}, dataType } = data;
    const title = formatObservableTitle({ data: obsData, attachment });

    const isFanged = useMemo(() => isAlreadyFang(data.data || data.attachment?.name || ''), [data]);

    const i = useLocalMessage();

    const tagReport = useMemo<ReportTagType[]>(
        () =>
            Object.entries(reports).reduce<ReportTagType[]>(
                (acc, [analyzerId, { taxonomies }], index) => [
                    ...acc,
                    ...taxonomies.map((taxonomy) => ({
                        ...taxonomy,
                        analyzerId,
                        index,
                    })),
                ],
                [],
            ),
        [reports],
    );

    const isCase = ObjectType.CASE === entityType && !!entityId;
    const linkTo = isCase
        ? RouteBuilder.compute(`cases.observable`, { observableId: observableId, caseId: entityId })
        : RouteBuilder.compute(`alerts.observable`, { observableId: observableId, alertId: entityId });

    const { coloredTags } = useContext(ScopeContext);

    return (
        <div className="observable-list-title">
            <div className={classList('observable-list-title__title')}>
                <ColorLabel
                    color="primary-color"
                    label={dataType}
                    type="outline"
                    onClick={!disabled ? () => onDataTypeClick?.(dataType) : undefined}
                />
                {isFanged && (
                    <THTooltip title={local('general.unfanged')} placement="top">
                        <ICONS.WarningOutlined className="observable-list-title__icon-secured" />
                    </THTooltip>
                )}

                <Link to={linkTo} className="observable-list-title__link">
                    {title}
                </Link>
            </div>

            {tagsIsOpen && (
                <div className="observable-list-title__tags">
                    <ICONS.TagsOutlined className="observable-list-title__icon" />
                    {tags.length ? (
                        <DisplayTag.List<string> data={tags} limit={5}>
                            {(tag) => (
                                <DisplayTag
                                    tag={tag}
                                    onClick={(tagValue, event): void => {
                                        const isAltPressed = event.altKey;
                                        onTagClick?.([tagValue], isAltPressed);
                                    }}
                                    color={coloredTags[tag]}
                                />
                            )}
                        </DisplayTag.List>
                    ) : (
                        i('general.none')
                    )}
                </div>
            )}

            {reportsIsOpen && (
                <div className="observable-list-title__reports">
                    <ICONS.ReferenceIcon className="observable-list-title__icon" />
                    {Object.keys(reports).length && eventList ? (
                        <DisplayTag.List<ReportTagType> data={tagReport}>
                            {({ level, namespace, predicate, value, index, analyzerId }) => (
                                <ObservableReportTag
                                    observableId={observableId}
                                    onGetLastReport={eventList?.onGetLastReport}
                                    analyzerId={analyzerId}
                                    observable={data}
                                    importObservable={isCase}
                                    eventList={eventList}
                                    value={formatTags({ namespace, predicate, value: value + '' })}
                                    color={ObservableReportLevelColors[level as ObservableReportLevel] as string}
                                    key={`report-${index}`}
                                />
                            )}
                        </DisplayTag.List>
                    ) : (
                        i('observable.list.table.value.no-reports')
                    )}
                </div>
            )}

            {onPreviewClick && (
                <div className="generic-drawer-preview">
                    <PreviewButton onClick={onPreviewClick} />
                </div>
            )}
        </div>
    );
};
